import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { QueryOptions } from '@shared/fetch-utils';
import { useMPEFetch, useMutateMPEFetch } from '@retail/app/hooks';
import { buildUrl } from '@shared/utils';
import { mpeApi } from '@retail/mgpris/config';
import {
  BaseCampaignItemPatchDTO,
  BaseCampaignPatchItemDTO,
  BaseCampaignPostItemDTO,
  BaseMgSupplierCampaignItemLookupDTO,
  BrandCampaignDTO,
  BrandCampaignItemDTO,
  BrandCampaignPatchDTO,
  BrandCampaignPostDTO,
  CampaignDTO,
  CampaignItemPriceOverrideDTO,
  CampaignType,
  MemberCampaignDTO,
  MemberCampaignItemDTO,
  MemberCampaignPatchDTO,
  MemberCampaignPostDTO,
  MgSupplierItemLookupDTO,
  MgSupplierItemMemberLookupDTO,
} from '@retail/campaign/types';
import { Brand } from '@retail/auth/types';

export type CampaignTenantType = 'brand' | 'member';

export interface CampaignsQueryParams {
  search?: string;
  brandKeys?: Brand['brandId'][];
  mandatory?: boolean;
  startDate?: string;
  endDate?: string;
  campaignType?: CampaignType;
}
type CampaignsProps = CampaignsQueryParams & QueryOptions;

export function useFetchBrandCampaigns({
  search,
  brandKeys,
  mandatory,
  startDate,
  endDate,
  campaignType,
  ...queryOptions
}: CampaignsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<CampaignDTO[]>({
    method: 'GET',
    queryKey: ['campaigns', search, brandKeys, campaignType, mandatory, startDate, endDate],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.getFullLinkPath(),
      searchParams: {
        'search-string': search,
        'brand-iamKeys': brandKeys,
        mandatory,
        type: campaignType,
        'start-date': startDate,
        'end-date': endDate,
      },
    }),
    ...queryOptions,
  });
}

interface MemberCampaignsProps extends CampaignsProps {
  assortmentId: number;
}

export function useFetchMemberCampaigns({
  search,
  brandKeys,
  mandatory,
  startDate,
  endDate,
  campaignType,
  assortmentId,
  ...queryOptions
}: MemberCampaignsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<MemberCampaignDTO[]>({
    method: 'GET',
    queryKey: ['campaigns', search, brandKeys, campaignType, mandatory, startDate, endDate],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortments.assortment.memberCampaigns.getFullLinkPath({ assortmentId }),
      searchParams: {
        'search-string': search,
        'brand-iamKeys': brandKeys,
        mandatory,
        type: campaignType,
        'start-date': startDate,
        'end-date': endDate,
      },
    }),
    ...queryOptions,
  });
}

export function useCreateCampaign(queryOptions: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, BrandCampaignPostDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.getFullLinkPath(),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

interface CreateMemberCampaignProps extends QueryOptions {
  assortmentId: number;
}

export function useCreateMemberCampaign({
  assortmentId,
  ...queryOptions
}: CreateMemberCampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, MemberCampaignPostDTO>({
    url: buildUrl({
      base: mpe.url,
      searchParams: { 'assortment-id': assortmentId },
      route: mpeApi.localCampaigns.getFullLinkPath(),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

export function useEditBrandCampaign({ campaignId, ...queryOptions }: { campaignId: string }) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, BrandCampaignPatchDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.campaign.getFullLinkPath({ campaignId }),
    }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

export function useEditMemberCampaign({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, MemberCampaignPatchDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.localCampaigns.localCampaign.getFullLinkPath({ campaignId }),
    }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

export function useEditCampaignBrands({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, { brandIds: string[] }>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.campaign.brands.getFullLinkPath({ campaignId }),
    }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

const deleteCampaignRoutes: Record<CampaignTenantType, (campaignId: string) => string> = {
  brand: (campaignId) => mpeApi.campaigns.campaign.getFullLinkPath({ campaignId }),
  member: (campaignId) => mpeApi.localCampaigns.localCampaign.getFullLinkPath({ campaignId }),
};

interface DeleteCampaignProps extends QueryOptions {
  campaignId: string;
  type: CampaignTenantType;
}

export function useDeleteCampaign({ campaignId, type, ...queryOptions }: DeleteCampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch({
    url: buildUrl({
      base: mpe.url,
      route: deleteCampaignRoutes[type](campaignId),
    }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
}

interface CampaignProps extends QueryOptions {
  campaignId: string;
}

interface MemberCampaignProps extends QueryOptions {
  campaignId: string;
  campaignType: CampaignType;
}

export function useFetchBrandCampaignDetails({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<BrandCampaignDTO>({
    method: 'GET',
    queryKey: ['campaigns', campaignId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.campaign.getFullLinkPath({ campaignId }),
    }),
    ...queryOptions,
  });
}

export function useFetchMemberCampaignDetails({
  campaignId,
  campaignType,
  ...queryOptions
}: MemberCampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<MemberCampaignDTO>({
    method: 'GET',
    queryKey: ['campaigns', campaignType, campaignId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.memberCampaigns.memberCampaign.getFullLinkPath({ campaignId }),
      searchParams: { 'campaign-type': campaignType },
    }),
    ...queryOptions,
  });
}

export function useFetchLocalCampaignDetails({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<MemberCampaignDTO>({
    method: 'GET',
    queryKey: ['campaigns', campaignId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.campaign.getFullLinkPath({ campaignId }),
    }),
    ...queryOptions,
  });
}

interface FetchCampaignProps {
  groupNumbers?: string[];
  discountGroupIds?: number[];
  supplierIds?: number[];
  query?: string;
}

export function useFetchBrandCampaignItems({
  campaignId,
  groupNumbers,
  discountGroupIds,
  supplierIds,
  query,
  ...queryOptions
}: CampaignProps & FetchCampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<BrandCampaignItemDTO[]>({
    method: 'GET',
    queryKey: [
      'campaigns',
      campaignId,
      'items',
      groupNumbers,
      discountGroupIds,
      supplierIds,
      query,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.campaigns.campaign.items.getFullLinkPath({ campaignId }),
      searchParams: {
        groupNumbers,
        discountGroupIds,
        supplierIds,
        query,
      },
    }),
    ...queryOptions,
  });
}

export function useFetchMemberCampaignItems({
  campaignId,
  campaignType,
  groupNumbers,
  discountGroupIds,
  supplierIds,
  query,
  ...queryOptions
}: MemberCampaignProps & FetchCampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<MemberCampaignItemDTO[]>({
    method: 'GET',
    queryKey: [
      'campaigns',
      campaignId,
      'items',
      campaignType,
      groupNumbers,
      discountGroupIds,
      supplierIds,
      query,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.memberCampaigns.memberCampaign.items.getFullLinkPath({
        campaignId,
      }),
      searchParams: {
        groupNumbers,
        discountGroupIds,
        supplierIds,
        'campaign-type': campaignType,
        query,
      },
    }),
    ...queryOptions,
  });
}

const updateItemRoutes: Record<
  CampaignTenantType,
  (campaignId: string, campaignItemId: string) => string
> = {
  brand: (campaignId, campaignItemId) =>
    mpeApi.campaigns.campaign.items.campaignItem.getFullLinkPath({
      campaignId,
      campaignItemId,
    }),
  member: (campaignId, campaignItemId) =>
    mpeApi.localCampaigns.localCampaign.items.campaignItem.getFullLinkPath({
      campaignId,
      campaignItemId,
    }),
};

interface UpdateItemProps extends CampaignProps {
  type: CampaignTenantType;
}

export function useUpdateCampaignItem<T extends BaseCampaignPatchItemDTO>({
  campaignId,
  type,
  ...queryOptions
}: UpdateItemProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, T, string>({
    url: (campaignItemId) =>
      buildUrl({
        base: mpe.url,
        route: updateItemRoutes[type](campaignId, campaignItemId),
      }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

export function useOverrideCampaignItemPrices({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, CampaignItemPriceOverrideDTO, string>({
    url: (campaignItemId) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.memberCampaigns.memberCampaign.items.campaignItem.getFullLinkPath({
          campaignId,
          campaignItemId,
        }),
      }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

export function useResetCampaignItemPrice({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, void, string>({
    url: (campaignItemId) =>
      buildUrl({
        base: mpe.url,
        route:
          mpeApi.memberCampaigns.memberCampaign.items.campaignItem.salesPriceOverride.getFullLinkPath(
            {
              campaignId,
              campaignItemId,
            }
          ),
      }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
}

const deleteItemRoutes: Record<
  CampaignTenantType,
  (campaignId: string, campaignItemId: string) => string
> = {
  brand: (campaignId, campaignItemId) =>
    mpeApi.campaigns.campaign.items.campaignItem.getFullLinkPath({ campaignId, campaignItemId }),
  member: (campaignId, campaignItemId) =>
    mpeApi.localCampaigns.localCampaign.items.campaignItem.getFullLinkPath({
      campaignId,
      campaignItemId,
    }),
};

interface DeleteItemProps extends QueryOptions {
  campaignId: string;
  type: CampaignTenantType;
}

export function useDeleteCampaignItem({ campaignId, type, ...queryOptions }: DeleteItemProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, void, string>({
    url: (campaignItemId) =>
      buildUrl({
        base: mpe.url,
        route: deleteItemRoutes[type](campaignId, campaignItemId),
      }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
}

const deleteItemsRoutes: Record<CampaignTenantType, string> = {
  brand: mpeApi.campaigns.items.getFullLinkPath(),
  member: mpeApi.localCampaigns.items.getFullLinkPath(),
};

export function useDeleteCampaignItems({ type, ...queryOptions }: DeleteItemProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, string[]>({
    url: buildUrl({
      base: mpe.url,
      route: deleteItemsRoutes[type],
    }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
}

export function useDistributeCampaign({ campaignId, ...queryOptions }: CampaignProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void>({
    url: (campaignId: string) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.campaigns.campaign.distribute.getFullLinkPath({ campaignId }),
      }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

const addItemsRoutes: Record<CampaignTenantType, (campaignId: string) => string> = {
  brand: (campaignId) => mpeApi.campaigns.campaign.items.getFullLinkPath({ campaignId }),
  member: (campaignId) => mpeApi.localCampaigns.localCampaign.items.getFullLinkPath({ campaignId }),
};

interface AddItemsProps extends CampaignProps {
  type: CampaignTenantType;
}

export function useAddCampaignItems<T extends BaseCampaignPostItemDTO>({
  type,
  campaignId,
  ...queryOptions
}: AddItemsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, T[]>({
    url: buildUrl({
      base: mpe.url,
      route: addItemsRoutes[type](campaignId),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

export function useEditCampaignItems<T extends BaseCampaignItemPatchDTO>({
  type,
  campaignId,
  ...queryOptions
}: AddItemsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, T[]>({
    url: buildUrl({
      base: mpe.url,
      route: addItemsRoutes[type](campaignId),
    }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

interface ValidateCampaignItemsProps extends QueryOptions {
  type: CampaignTenantType;
}

const validateRoutes: Record<CampaignTenantType, string> = {
  brand: mpeApi.campaigns.validateCampaignItems.fullRoutePath,
  member: mpeApi.campaigns.validateCampaignItems.fullRoutePath, // todo: egen route
};

export function useValidateCampaignItems<T extends BaseCampaignPostItemDTO>({
  type,
  ...queryOptions
}: ValidateCampaignItemsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<{ validItems: T[]; invalidItems: T[] }, T[]>({
    url: buildUrl({
      base: mpe.url,
      route: validateRoutes[type],
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

interface LookupProps extends QueryOptions {
  assortmentId: number;
}

export function useLookupCampaignItems<T extends BaseMgSupplierCampaignItemLookupDTO>({
  ...queryOptions
}: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<T[], MgSupplierItemLookupDTO[]>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.mgSupplierItems.lookup.campaignItem.getFullLinkPath(),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

export function useLookupMemberCampaignItems<T extends BaseMgSupplierCampaignItemLookupDTO>({
  assortmentId,
  ...queryOptions
}: LookupProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<T[], MgSupplierItemMemberLookupDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.mgSupplierItems.lookup.memberCampaignItem.getFullLinkPath(),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

interface EnrollmentProps extends CampaignProps {
  assortmentId: number;
}

export function useCampaignEnrollmentApi({
  assortmentId,
  campaignId,
  ...queryOptions
}: EnrollmentProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, { shouldEnroll: boolean }>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortments.assortment.memberCampaigns.campaign.enrollment.getFullLinkPath({
        assortmentId,
        campaignId,
      }),
    }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}
